import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Stack,
} from "@mui/material";
import { toast } from "react-toastify";
import PasswordConfirmation from "./PasswordConfirmation";
import useGetESignReasonTypes from "../../hooks/useGetESignReasonTypes";
import useUpdateBlockStatusMutation from "../../hooks/useUpdateBlockStatusMutation";

const croStatus = ["InComplete", "Complete"];
const dmStatus = ["UnFrozen", "Frozen"];

const defaultBlockStatuses = {
  invStatus: "Select an Option",
  croStatus: "InComplete",
  dmStatus: "UnFrozen",
};

const UpdateStatusPopUp = ({
  showModal,
  setShowModal,
  blocksSelected,
  inputType,
  queryLogData,
  infoData,
  visit_number,
  trialId,
  patient_id,
}) => {
  const [blockStatuses, setBlockStatuses] = useState(defaultBlockStatuses);
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const updateBlockStatusMutation = useUpdateBlockStatusMutation();
  const [modalShow, setModalShow] = useState(false);
  const [certificationStatus, setCertificationStatus] = useState("");
  const [isCorrectPass, setIsCorrectPass] = useState(false);
  const [esignValue, setEsignValue] = useState("");
  const [invStatus, setInvStatus] = useState(["Certified", "UnCertified"]);

  const getInvStatusOptions = (queryLogData, blocksSelected) => {
    if (!queryLogData || !blocksSelected || blocksSelected.length === 0) {
      return ["Certified", "UnCertified"];
    }
    const selectedBlock = queryLogData.find((item) => item.id === blocksSelected[0]);

    if (!selectedBlock || !selectedBlock.status_object) {
      return ["Certified", "UnCertified"];
    }

    const { inv_certified_bool, dm_freeze_bool } = selectedBlock.status_object;

    // Once frozen, uncertifying is blocked — only show the current certified state
    if (dm_freeze_bool === true) {
      return ["Certified"];
    }

    if (inv_certified_bool === true) {
      return ["UnCertified"];
    } else if (inv_certified_bool === false) {
      return ["Certified"];
    } else {
      return ["Certified", "UnCertified"];
    }
  };

  useEffect(() => {
    if (queryLogData && blocksSelected) {
      setInvStatus(getInvStatusOptions(queryLogData, blocksSelected));
      setBlockStatuses((prev) => ({ ...prev, invStatus: "Select an Option" }));
    }
  }, [queryLogData, blocksSelected]);

  const { data: ESignReasonType = [] } = useGetESignReasonTypes();

  const blockStatusChange = (event, type) => {
    const { value } = event.target;
    setBlockStatuses((prev) => ({ ...prev, [type]: value }));
    if (type === "invStatus") {
      setCertificationStatus(value);
    }

    if (type === "invStatus" && (value === "Certified" || value === "UnCertified")) {
      setCertificationStatus(value);
      setModalShow(true);
      setEsignValue(value);

      if (infoData?.ecrf_password_authentication) {
        setModalShow(true);
      } else {
        setModalShow(false);
        setCertificationStatus(value);
        setEsignValue(value);
      }
    }
  };

  const handleCancelClick = () => {
    setCertificationStatus("");
    setBlockStatuses((prev) => ({ ...prev, invStatus: "Select an Option" }));
    setShowModal(false);
  };

  const handleClose = () => {
    setShowModal(false);
    setCertificationStatus("");
    setBlockStatuses((prev) => ({ ...prev, invStatus: "Select an Option" }));
  };

  const handleUpdateBlockStatus = () => {
    let inputBody = {};
    if (["Investigator", "Super Admin"].includes(user_data.role) && inputType === "eSig") {
      inputBody.invStatus = blockStatuses.invStatus;
      inputBody.e_sign_certified = esignValue;
    }
    if (inputType === "eSig") {
      inputBody.e_sign_certified = blockStatuses.e_sign_certified;
    }
    if (
      ["CRO Admin", "Data Manager", "Super Admin"].includes(user_data.role) &&
      inputType === "cro_status"
    ) {
      inputBody.croStatus = blockStatuses.croStatus;
    }
    if (["Data Manager", "Super Admin"].includes(user_data.role) && inputType === "freeze") {
      inputBody.dmStatus = blockStatuses.dmStatus;
    }

    updateBlockStatusMutation.mutate(
      {
        blocksSelected,
        blockStatuses: inputBody,
        visit_number: visit_number,
        trialId: trialId,
        patient_id: patient_id,
      },
      {
        onSuccess: () => {
          toast.success("Status Changed Successfully");
          setShowModal(false);
          setCertificationStatus("");
        },
        onError: (err) => toast.error(err.response?.data?.error || "An error occurred"),
      }
    );
  };

  return (
    <>
      <Dialog
        open={showModal}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        data-testid="update-status-dialog"
      >
        <DialogTitle>Update Block Status</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {["Investigator", "Super Admin"].includes(user_data.role) && inputType === "eSig" && (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel>Certification Status</InputLabel>
                  <Select
                    label="Certification Status"
                    value={blockStatuses.invStatus}
                    onChange={(e) => blockStatusChange(e, "invStatus")}
                    SelectDisplayProps={{ "data-testid": "certification-status-select-trigger" }}
                  >
                    <MenuItem value="Select an Option" disabled>
                      Select an Option
                    </MenuItem>
                    {invStatus.map((name) => (
                      <MenuItem key={name} value={name}>
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {certificationStatus && (
                  <FormControl size="small" fullWidth>
                    <InputLabel>E-Sign Reason</InputLabel>
                    <Select
                      label="E-Sign Reason"
                      value={blockStatuses.e_sign_certified}
                      onChange={(e) => blockStatusChange(e, "e_sign_certified")}
                      SelectDisplayProps={{ "data-testid": "esign-reason-select-trigger" }}
                    >
                      {ESignReasonType?.filter(
                        ({ reason_type }) => reason_type === blockStatuses.invStatus
                      ).map(({ id, title }) => (
                        <MenuItem key={id} value={id}>
                          <ListItemText primary={title} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}

            {["CRO Admin", "Data Manager", "Super Admin"].includes(user_data.role) &&
              inputType === "cro_status" && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Block Status</InputLabel>
                  <Select
                    label="Block Status"
                    value={blockStatuses.croStatus}
                    onChange={(e) => blockStatusChange(e, "croStatus")}
                    SelectDisplayProps={{ "data-testid": "block-status-select-trigger" }}
                  >
                    {croStatus.map((name) => (
                      <MenuItem key={name} value={name}>
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

            {["Data Manager", "Super Admin"].includes(user_data.role) && inputType === "freeze" && (
              <FormControl size="small" fullWidth>
                <InputLabel>Freeze Status</InputLabel>
                <Select
                  label="Freeze Status"
                  value={blockStatuses.dmStatus}
                  onChange={(e) => blockStatusChange(e, "dmStatus")}
                  SelectDisplayProps={{ "data-testid": "freeze-status-select-trigger" }}
                >
                  {dmStatus.map((name) => (
                    <MenuItem key={name} value={name}>
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleUpdateBlockStatus}
            data-testid="update-status-submit-btn"
          >
            Update
          </Button>
        </DialogActions>

        {modalShow && (
          <PasswordConfirmation
            show={modalShow}
            onHide={() => {
              setModalShow(false);
              setCertificationStatus("");
            }}
            setIsCorrectPass={setIsCorrectPass}
            handleCancelClick={handleCancelClick}
            setCertificationStatus={setCertificationStatus}
            onHideonSuccess={() => setModalShow(false)}
          />
        )}
      </Dialog>
    </>
  );
};

export default UpdateStatusPopUp;
